/**
 * EmbedStep — fourth step of the onboarding wizard.
 *
 * Serves two personas on a single page:
 * - Technical users copy the code snippet directly.
 * - Non-technical users copy a pre-composed message to forward to
 *   their developer/agency/IT team.
 *
 * Advanced embed formats live on the canary detail page after onboarding.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { buildBootstrapScriptUrl, DETECTION_DOMAINS } from '@spoofio/shared'
import { Button } from '@/components/ui'
import { ReviewSummary } from './ReviewSummary'

type EmbedStepProps = {
  canaryToken: string
  orgId: string
  orgName: string
  canaryId: string
  canaryName: string
  authorizedDomain: string
  authorizedDomainId: string
  onOrgNameUpdated: (name: string) => void
  onCanaryNameUpdated: (name: string) => void
  onDomainUpdated: (domain: string) => void
  onNext: () => void
  headingRef?: RefObject<HTMLHeadingElement>
}

const COPY_RESET_MS = 3000
const EMBED_PATH = '/dist/embed.js'

function buildInstructionMessage(embedCode: string, canaryName: string, domain: string): string {
  return [
    'Hi \u2014 please add the following script tag to our website.',
    '',
    'Paste it inside the <head> tag, before the closing </head>, on every',
    'page that should be protected. It loads a lightweight, invisible',
    'detection script.',
    '',
    embedCode,
    '',
    `Page being protected: ${canaryName} (${domain})`,
    '',
    'Thanks!',
  ].join('\n')
}

export function EmbedStep({
  canaryToken, orgId, orgName, canaryId, canaryName,
  authorizedDomain, authorizedDomainId,
  onOrgNameUpdated, onCanaryNameUpdated, onDomainUpdated,
  onNext, headingRef,
}: EmbedStepProps) {
  const [copiedCode, setCopiedCode] = useState(false)
  const [copiedInstructions, setCopiedInstructions] = useState(false)
  const codeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const instrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const embedCode = useMemo(() => {
    const url = buildBootstrapScriptUrl({
      baseUrl: DETECTION_DOMAINS.PRODUCTION,
      path: EMBED_PATH,
      token: canaryToken,
    })
    return `<script src="${url}"></script>`
  }, [canaryToken])

  const instructionMessage = useMemo(
    () => buildInstructionMessage(embedCode, canaryName, authorizedDomain),
    [embedCode, canaryName, authorizedDomain]
  )

  useEffect(() => {
    return () => {
      if (codeTimerRef.current) clearTimeout(codeTimerRef.current)
      if (instrTimerRef.current) clearTimeout(instrTimerRef.current)
    }
  }, [])

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopiedCode(true)
      if (codeTimerRef.current) clearTimeout(codeTimerRef.current)
      codeTimerRef.current = setTimeout(() => { setCopiedCode(false) }, COPY_RESET_MS)
    } catch {
      setCopiedCode(false)
    }
  }, [embedCode])

  const handleCopyInstructions = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(instructionMessage)
      setCopiedInstructions(true)
      if (instrTimerRef.current) clearTimeout(instrTimerRef.current)
      instrTimerRef.current = setTimeout(() => { setCopiedInstructions(false) }, COPY_RESET_MS)
    } catch {
      setCopiedInstructions(false)
    }
  }, [instructionMessage])

  return (
    <section
      role="form"
      aria-label="Embed your canary"
      className="max-w-2xl mx-auto space-y-6 py-8"
    >
      <div className="text-center space-y-3">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl font-bold text-gray-900 tracking-tight focus:outline-none"
        >
          Install your canary
        </h2>
        <p className="text-gray-600">
          Add this one line of code to your website. If someone else manages
          your site, you can send them the instructions below.
        </p>
      </div>

      <ReviewSummary
        orgId={orgId}
        orgName={orgName}
        canaryId={canaryId}
        canaryName={canaryName}
        authorizedDomain={authorizedDomain}
        authorizedDomainId={authorizedDomainId}
        onOrgNameUpdated={onOrgNameUpdated}
        onCanaryNameUpdated={onCanaryNameUpdated}
        onDomainUpdated={onDomainUpdated}
      />

      <pre
        role="code"
        aria-label="Embed code snippet"
        data-testid="embed-code-block"
        className="bg-gray-900 text-gray-100 text-sm rounded-lg p-4 overflow-x-auto whitespace-nowrap font-mono"
      >
        <code>{embedCode}</code>
      </pre>

      <p className="text-sm text-gray-500">
        Paste this inside the{' '}
        <code className="text-sm bg-gray-100 text-gray-700 px-1 py-0.5 rounded">&lt;head&gt;</code>{' '}
        tag on every page you want to protect. It loads invisibly &mdash;
        visitors won&apos;t see anything.
      </p>

      <div className="flex justify-center">
        <Button
          size="lg"
          onClick={() => { void handleCopyCode() }}
          className="w-full sm:w-auto min-h-[44px]"
        >
          {copiedCode ? 'Copied!' : 'Copy Code'}
        </Button>
      </div>

      {/* Divider */}
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-sm text-gray-500">or send to your developer</span>
        </div>
      </div>

      {/* Hand-off card */}
      <div
        data-testid="handoff-card"
        className="rounded-lg border border-gray-200 bg-gray-50 p-5 space-y-3"
      >
        <p className="font-semibold text-gray-900">Have someone else install it?</p>
        <p className="text-sm text-gray-600">
          Copy the message below and send it to whoever manages your
          website &mdash; your developer, agency, or IT team. It includes the
          code and placement instructions.
        </p>
        <Button
          variant="secondary"
          onClick={() => { void handleCopyInstructions() }}
          className="min-h-[44px]"
        >
          {copiedInstructions ? 'Copied!' : 'Copy Instructions'}
        </Button>
      </div>

      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="copy-live-region"
      >
        {copiedCode ? 'Code copied to clipboard' : ''}
        {copiedInstructions ? 'Instructions copied to clipboard' : ''}
      </div>

      <p className="text-sm text-gray-500 text-center">
        You can always find this code and these instructions on the canary detail page.
      </p>

      <div className="flex justify-center pt-4">
        <Button
          variant="secondary"
          onClick={onNext}
          className="w-full sm:w-auto min-h-[44px]"
        >
          Continue
        </Button>
      </div>
    </section>
  )
}
